import { useState, useEffect, useMemo } from 'react';

interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  touchDevice: boolean;
}

export const useDevice = (): DeviceInfo => {
  const [screenDimensions, setScreenDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  // Memoize device calculations to prevent unnecessary recalculations
  const deviceInfo = useMemo(() => {
    const { width, height } = screenDimensions;
    
    const isMobile = width < 768;
    const isTablet = width >= 768 && width < 1024;
    const isDesktop = width >= 1024;
    const orientation: 'portrait' | 'landscape' = width > height ? 'landscape' : 'portrait';
    const touchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    return {
      isMobile,
      isTablet,
      isDesktop,
      screenWidth: width,
      screenHeight: height,
      orientation,
      touchDevice
    };
  }, [screenDimensions]);

  // Throttled update function to prevent excessive re-renders
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateDeviceInfo = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScreenDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 100); // 100ms throttle
    };

    // Initial update
    updateDeviceInfo();

    // Event listeners
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
};

// Utility functions for quick checks
export const isMobileDevice = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth < 768;
};

export const isTabletDevice = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1024;
};

export const isDesktopDevice = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth >= 1024;
};

export const isTouchDevice = (): boolean => {
  return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
};
